// ══════════════════════════════════════════════════════════════════════════════
// CRÉATEUR DE STORY - Upload photo/vidéo + ajout texte/emoji
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Camera, Video, Type, Smile, Send, Loader2, Plus,
  Bold, Italic, Palette, Trash2, Move, RotateCw, Check,
  Image, Upload, MapPin, ChevronLeft
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { API_URL } from '../../config/api';

// Emojis populaires pour le BTP
const QUICK_EMOJIS = ['🏗️', '🔨', '⚡', '🔧', '🏠', '🚿', '🌿', '🏊', '💪', '✅', '🔥', '⭐', '👷', '📐', '🧱', '🪵', '🎨', '💡', '🔩', '🛠️'];

// Couleurs de texte
const TEXT_COLORS = [
  '#FFFFFF', '#000000', '#E8650A', '#1E3A5F', '#10B981',
  '#EF4444', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4'
];

// Couleurs de fond
const BG_COLORS = [
  'transparent',
  'rgba(0,0,0,0.5)',
  'rgba(0,0,0,0.8)',
  'rgba(255,255,255,0.8)',
  'rgba(232,101,10,0.8)',
  'rgba(30,58,95,0.8)'
];

export default function CreateStoryPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);
  
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null); // 'photo' ou 'video'
  const [videoDuration, setVideoDuration] = useState(0);
  
  const [textOverlays, setTextOverlays] = useState([]);
  const [activeOverlay, setActiveOverlay] = useState(null);
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  const [newText, setNewText] = useState('');
  const [newTextColor, setNewTextColor] = useState('#FFFFFF');
  const [newTextBg, setNewTextBg] = useState('rgba(0,0,0,0.5)');
  const [newTextSize, setNewTextSize] = useState(24);
  const [newTextStyle, setNewTextStyle] = useState('bold');
  
  const [caption, setCaption] = useState('');
  const [location, setLocation] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Lien produit/prestation
  const [linkedProduct, setLinkedProduct] = useState(null);
  const [linkedService, setLinkedService] = useState(null);
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [myProducts, setMyProducts] = useState([]);
  const [myServices, setMyServices] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  
  // Charger les produits/prestations de l'utilisateur
  const loadMyProductsAndServices = async () => {
    setLoadingLinks(true);
    try {
      const userId = localStorage.getItem('user_id') || 'demo_user';
      
      // Charger les produits
      const prodRes = await fetch(`${API_URL}/api/cockpit/products?seller_id=${userId}&limit=20`);
      if (prodRes.ok) {
        const prodData = await prodRes.json();
        setMyProducts(prodData.products || []);
      }
      
      // Charger les prestations
      const servRes = await fetch(`${API_URL}/api/cockpit/prestations?user_id=${userId}&limit=20`);
      if (servRes.ok) {
        const servData = await servRes.json();
        setMyServices(servData.prestations || []);
      }
    } catch (e) {
      console.error('Error loading products/services:', e);
    } finally {
      setLoadingLinks(false);
    }
  };
  
  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    
    if (!isVideo && !isImage) {
      toast.error('Format non supporté. Utilise une image ou une vidéo.');
      return;
    }
    
    if (file.size > 50 * 1024 * 1024) { // 50MB max
      toast.error('Fichier trop volumineux (max 50MB)');
      return;
    }
    
    setMediaFile(file);
    setMediaType(isVideo ? 'video' : 'photo');
    setMediaPreview(URL.createObjectURL(file));
    
    // Check video duration
    if (isVideo) {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        if (video.duration > 90) {
          toast.error('La vidéo ne peut pas dépasser 90 secondes');
          setMediaFile(null);
          setMediaPreview(null);
          setMediaType(null);
        } else {
          setVideoDuration(Math.round(video.duration));
        }
      };
      video.src = URL.createObjectURL(file);
    }
  };
  
  // Add text overlay
  const addTextOverlay = () => {
    if (!newText.trim()) return;
    
    const overlay = {
      id: `txt-${Date.now()}`,
      text: newText,
      x: 50,
      y: 50,
      font_size: newTextSize,
      color: newTextColor,
      background_color: newTextBg === 'transparent' ? null : newTextBg,
      rotation: 0,
      font_style: newTextStyle
    };
    
    setTextOverlays(prev => [...prev, overlay]);
    setNewText('');
    setShowTextEditor(false);
    toast.success('Texte ajouté !');
  };
  
  // Add emoji overlay
  const addEmoji = (emoji) => {
    const overlay = {
      id: `emoji-${Date.now()}`,
      text: emoji,
      x: 30 + Math.random() * 40,
      y: 30 + Math.random() * 40,
      font_size: 48,
      color: '#FFFFFF',
      background_color: null,
      rotation: (Math.random() - 0.5) * 20,
      font_style: 'normal'
    };
    
    setTextOverlays(prev => [...prev, overlay]);
    setShowEmojiPicker(false);
  };
  
  // Remove overlay
  const removeOverlay = (id) => {
    setTextOverlays(prev => prev.filter(o => o.id !== id));
    setActiveOverlay(null);
  };
  
  // Drag overlay
  const handleOverlayDrag = (e, overlay) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setTextOverlays(prev => prev.map(o => 
      o.id === overlay.id ? { ...o, x: Math.max(5, Math.min(95, x)), y: Math.max(5, Math.min(95, y)) } : o
    ));
  };
  
  // Upload and create story
  const handlePublish = async () => {
    if (!mediaFile) {
      toast.error('Ajoute une photo ou vidéo');
      return;
    }
    
    setUploading(true);
    
    try {
      // For demo, use a placeholder URL (in production, upload to R2/S3)
      // Convert file to base64 for demo
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        
        // Create story via API
        const response = await fetch(`${API_URL}/api/reseau/stories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            media_type: mediaType,
            media_url: mediaPreview, // In production, this would be the uploaded URL
            thumbnail_url: mediaType === 'video' ? mediaPreview : null,
            duration: mediaType === 'video' ? videoDuration : null,
            text_overlays: textOverlays,
            caption: caption || null,
            location: location || null,
            metier: null,
            linked_product: linkedProduct,
            linked_service: linkedService
          })
        });
        
        if (response.ok) {
          toast.success('Story publiée ! Visible pendant 7 jours.');
          navigate('/reseau');
        } else {
          throw new Error('Erreur création story');
        }
      };
      reader.readAsDataURL(mediaFile);
      
    } catch (error) {
      console.error('Error creating story:', error);
      toast.error('Erreur lors de la publication');
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-3 bg-black/80 backdrop-blur">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2">
          <ChevronLeft size={28} />
        </button>
        <h1 className="text-lg font-semibold">Créer une story</h1>
        <Button
          onClick={handlePublish}
          disabled={!mediaFile || uploading}
          className="bg-[#E8650A] hover:bg-[#D55A09] text-white px-4 py-2 rounded-full"
        >
          {uploading ? <Loader2 size={18} className="animate-spin" /> : 'Publier'}
        </Button>
      </div>
      
      {/* Main content */}
      <div className="pt-16 pb-24">
        {!mediaPreview ? (
          // Upload zone
          <div className="flex flex-col items-center justify-center min-h-[70vh] px-6">
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="w-full max-w-sm aspect-[9/16] rounded-2xl border-2 border-dashed border-gray-600 flex flex-col items-center justify-center gap-4 cursor-pointer hover:border-[#E8650A] transition-colors"
            >
              <div className="w-20 h-20 rounded-full bg-gray-800 flex items-center justify-center">
                <Camera size={36} className="text-gray-400" />
              </div>
              <div className="text-center">
                <p className="text-lg font-medium">Ajoute une photo ou vidéo</p>
                <p className="text-sm text-gray-500 mt-1">Vidéos max 90 secondes</p>
              </div>
              <div className="flex gap-3 mt-2">
                <div className="flex items-center gap-1.5 text-sm text-gray-400">
                  <Image size={16} /> Photo
                </div>
                <div className="flex items-center gap-1.5 text-sm text-gray-400">
                  <Video size={16} /> Vidéo
                </div>
              </div>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        ) : (
          // Preview + editor
          <div className="relative">
            {/* Media preview container */}
            <div 
              ref={containerRef}
              className="relative w-full max-w-[430px] mx-auto aspect-[9/16] bg-gray-900 overflow-hidden"
            >
              {mediaType === 'video' ? (
                <video
                  src={mediaPreview}
                  className="w-full h-full object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              ) : (
                <img
                  src={mediaPreview}
                  alt=""
                  className="w-full h-full object-cover"
                />
              )}
              
              {/* Text overlays */}
              {textOverlays.map((overlay) => (
                <div
                  key={overlay.id}
                  className={`absolute cursor-move select-none ${activeOverlay === overlay.id ? 'ring-2 ring-white ring-offset-2 ring-offset-black' : ''}`}
                  style={{
                    left: `${overlay.x}%`,
                    top: `${overlay.y}%`,
                    transform: `translate(-50%, -50%) rotate(${overlay.rotation || 0}deg)`,
                  }}
                  onClick={() => setActiveOverlay(activeOverlay === overlay.id ? null : overlay.id)}
                  draggable
                  onDrag={(e) => handleOverlayDrag(e, overlay)}
                >
                  <span
                    style={{
                      fontSize: `${overlay.font_size || 24}px`,
                      color: overlay.color || '#FFFFFF',
                      backgroundColor: overlay.background_color || 'transparent',
                      fontWeight: overlay.font_style === 'bold' ? 'bold' : 'normal',
                      fontStyle: overlay.font_style === 'italic' ? 'italic' : 'normal',
                      padding: overlay.background_color ? '6px 12px' : '2px',
                      borderRadius: overlay.background_color ? '8px' : '0',
                      textShadow: !overlay.background_color ? '0 2px 4px rgba(0,0,0,0.8)' : 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {overlay.text}
                  </span>
                  
                  {/* Delete button when active */}
                  {activeOverlay === overlay.id && (
                    <button
                      onClick={(e) => { e.stopPropagation(); removeOverlay(overlay.id); }}
                      className="absolute -top-3 -right-3 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              
              {/* Duration badge for video */}
              {mediaType === 'video' && (
                <div className="absolute top-4 right-4 bg-black/60 text-white text-sm px-2 py-1 rounded-full">
                  {Math.floor(videoDuration / 60)}:{(videoDuration % 60).toString().padStart(2, '0')}
                </div>
              )}
            </div>
            
            {/* Toolbar */}
            <div className="fixed bottom-0 left-0 right-0 bg-black/90 backdrop-blur px-4 py-4 space-y-3">
              {/* Caption input */}
              <input
                type="text"
                placeholder="Ajoute une légende..."
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className="w-full bg-gray-800 text-white px-4 py-2.5 rounded-xl text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#E8650A]"
              />
              
              {/* Action buttons */}
              <div className="flex items-center justify-between">
                <div className="flex gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                  <button
                    onClick={() => setShowTextEditor(true)}
                    className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-full text-sm flex-shrink-0"
                  >
                    <Type size={18} /> Texte
                  </button>
                  <button
                    onClick={() => setShowEmojiPicker(true)}
                    className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-full text-sm flex-shrink-0"
                  >
                    <Smile size={18} /> Emoji
                  </button>
                  <button
                    onClick={() => { loadMyProductsAndServices(); setShowLinkPicker(true); }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm flex-shrink-0 ${
                      linkedProduct || linkedService 
                        ? 'bg-[#E8650A] text-white' 
                        : 'bg-gray-800'
                    }`}
                  >
                    <Upload size={18} /> {linkedProduct || linkedService ? 'Lié' : 'Lier'}
                  </button>
                </div>
                
                <button
                  onClick={() => { setMediaFile(null); setMediaPreview(null); setTextOverlays([]); setLinkedProduct(null); setLinkedService(null); }}
                  className="p-2 text-gray-400 hover:text-white flex-shrink-0"
                >
                  <Trash2 size={20} />
                </button>
              </div>
              
              {/* Affichage du lien actif */}
              {(linkedProduct || linkedService) && (
                <div className="flex items-center gap-2 bg-gray-800/50 rounded-lg p-2 mt-2">
                  <div className="w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center">
                    {linkedProduct ? (
                      linkedProduct.image_url ? (
                        <img src={linkedProduct.image_url} alt="" className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <Upload size={16} className="text-gray-400" />
                      )
                    ) : (
                      <Upload size={16} className="text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {linkedProduct?.name || linkedService?.name}
                    </p>
                    <p className="text-xs text-gray-400">
                      {linkedProduct ? 'Produit' : 'Prestation'}
                      {linkedProduct?.price && ` • ${linkedProduct.price}€`}
                      {linkedService?.price_from && ` • À partir de ${linkedService.price_from}€`}
                    </p>
                  </div>
                  <button
                    onClick={() => { setLinkedProduct(null); setLinkedService(null); }}
                    className="p-1 text-gray-400 hover:text-white"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Text Editor Modal */}
      <AnimatePresence>
        {showTextEditor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/90 flex items-end"
            onClick={() => setShowTextEditor(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              className="w-full bg-gray-900 rounded-t-2xl p-4 space-y-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Ajouter du texte</h3>
                <button onClick={() => setShowTextEditor(false)}>
                  <X size={24} />
                </button>
              </div>
              
              <input
                type="text"
                placeholder="Écris ton texte..."
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-xl text-lg placeholder:text-gray-500 focus:outline-none"
                autoFocus
              />
              
              {/* Color picker */}
              <div>
                <p className="text-sm text-gray-400 mb-2">Couleur du texte</p>
                <div className="flex gap-2">
                  {TEXT_COLORS.map((color) => (
                    <button
                      key={color}
                      onClick={() => setNewTextColor(color)}
                      className={`w-8 h-8 rounded-full border-2 ${newTextColor === color ? 'border-white' : 'border-transparent'}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              
              {/* Background color */}
              <div>
                <p className="text-sm text-gray-400 mb-2">Fond</p>
                <div className="flex gap-2">
                  {BG_COLORS.map((color) => (
                    <button
                      key={color}
                      onClick={() => setNewTextBg(color)}
                      className={`w-8 h-8 rounded-full border-2 ${newTextBg === color ? 'border-white' : 'border-gray-600'}`}
                      style={{ backgroundColor: color === 'transparent' ? 'transparent' : color }}
                    >
                      {color === 'transparent' && <X size={16} className="m-auto text-gray-500" />}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Style buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => setNewTextStyle(newTextStyle === 'bold' ? 'normal' : 'bold')}
                  className={`flex-1 py-2 rounded-lg ${newTextStyle === 'bold' ? 'bg-white text-black' : 'bg-gray-800'}`}
                >
                  <Bold size={20} className="mx-auto" />
                </button>
                <button
                  onClick={() => setNewTextStyle(newTextStyle === 'italic' ? 'normal' : 'italic')}
                  className={`flex-1 py-2 rounded-lg ${newTextStyle === 'italic' ? 'bg-white text-black' : 'bg-gray-800'}`}
                >
                  <Italic size={20} className="mx-auto" />
                </button>
                <button
                  onClick={() => setNewTextSize(Math.min(48, newTextSize + 4))}
                  className="flex-1 py-2 rounded-lg bg-gray-800 text-lg font-bold"
                >
                  A+
                </button>
                <button
                  onClick={() => setNewTextSize(Math.max(16, newTextSize - 4))}
                  className="flex-1 py-2 rounded-lg bg-gray-800 text-sm"
                >
                  A-
                </button>
              </div>
              
              <Button
                onClick={addTextOverlay}
                disabled={!newText.trim()}
                className="w-full bg-[#E8650A] hover:bg-[#D55A09] py-3"
              >
                Ajouter
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Emoji Picker Modal */}
      <AnimatePresence>
        {showEmojiPicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/90 flex items-end"
            onClick={() => setShowEmojiPicker(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              className="w-full bg-gray-900 rounded-t-2xl p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Ajouter un emoji</h3>
                <button onClick={() => setShowEmojiPicker(false)}>
                  <X size={24} />
                </button>
              </div>
              
              <div className="grid grid-cols-10 gap-2">
                {QUICK_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => addEmoji(emoji)}
                    className="text-3xl p-2 hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Link Product/Service Picker */}
      <AnimatePresence>
        {showLinkPicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/90 flex items-end"
            onClick={() => setShowLinkPicker(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              className="bg-gray-900 w-full rounded-t-3xl p-5 max-h-[70vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Lier un produit ou prestation</h3>
                <button onClick={() => setShowLinkPicker(false)} className="p-1">
                  <X size={24} />
                </button>
              </div>
              
              {loadingLinks ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-8 h-8 border-2 border-[#E8650A] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="overflow-y-auto max-h-[55vh] space-y-4" style={{ scrollbarWidth: 'none' }}>
                  {/* Section Produits */}
                  {myProducts.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2">
                        <Upload size={14} /> Mes produits
                      </h4>
                      <div className="space-y-2">
                        {myProducts.map((product) => (
                          <button
                            key={product.id}
                            onClick={() => {
                              setLinkedProduct({
                                id: product.id,
                                product_id: product.id,
                                name: product.title || product.name,
                                image_url: product.images?.[0] || product.thumbnail,
                                price: product.promo_price || product.price
                              });
                              setLinkedService(null);
                              setShowLinkPicker(false);
                            }}
                            className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                              linkedProduct?.id === product.id 
                                ? 'bg-[#E8650A]/20 border border-[#E8650A]' 
                                : 'bg-gray-800 hover:bg-gray-700'
                            }`}
                          >
                            <div className="w-12 h-12 bg-gray-700 rounded-lg flex-shrink-0 overflow-hidden">
                              {(product.images?.[0] || product.thumbnail) ? (
                                <img 
                                  src={product.images?.[0] || product.thumbnail} 
                                  alt="" 
                                  className="w-full h-full object-cover" 
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Upload size={20} className="text-gray-500" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <p className="font-medium truncate">{product.title || product.name}</p>
                              <p className="text-sm text-gray-400">
                                {product.promo_price ? (
                                  <>
                                    <span className="text-[#E8650A] font-semibold">{product.promo_price}€</span>
                                    <span className="line-through ml-2">{product.price}€</span>
                                  </>
                                ) : (
                                  <span>{product.price}€</span>
                                )}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Section Prestations */}
                  {myServices.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2">
                        <Upload size={14} /> Mes prestations
                      </h4>
                      <div className="space-y-2">
                        {myServices.map((service) => (
                          <button
                            key={service.id}
                            onClick={() => {
                              setLinkedService({
                                id: service.id,
                                prestation_id: service.id,
                                name: service.title || service.name,
                                price_from: service.price_min || service.price
                              });
                              setLinkedProduct(null);
                              setShowLinkPicker(false);
                            }}
                            className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                              linkedService?.id === service.id 
                                ? 'bg-[#E8650A]/20 border border-[#E8650A]' 
                                : 'bg-gray-800 hover:bg-gray-700'
                            }`}
                          >
                            <div className="w-12 h-12 bg-[#1E3A5F] rounded-lg flex-shrink-0 flex items-center justify-center">
                              <Upload size={20} className="text-white" />
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <p className="font-medium truncate">{service.title || service.name}</p>
                              <p className="text-sm text-gray-400">
                                À partir de {service.price_min || service.price}€
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Aucun produit/prestation */}
                  {myProducts.length === 0 && myServices.length === 0 && (
                    <div className="text-center py-10">
                      <div className="w-16 h-16 bg-gray-800 rounded-full mx-auto mb-4 flex items-center justify-center">
                        <Upload size={28} className="text-gray-500" />
                      </div>
                      <p className="text-gray-400 mb-2">Aucun produit ou prestation</p>
                      <p className="text-sm text-gray-500">Ajoute des produits/prestations depuis ton Cockpit Pro</p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
